import documentEditorService from '../services/documentEditor.service.js';

/**
 * Extracts a subsection from a parent section's content
 * @param {string} content - The parent section's HTML/text content
 * @param {string} subsectionTitle - The subsection title to extract (e.g., "2.1 Purpose", "2.2 Risk-Benefit")
 * @returns {string} - Extracted subsection content or empty string
 */
const extractSubsection = (content, subsectionTitle) => {
  if (!content || !subsectionTitle) return '';
  
  // Normalize subsection title for matching
  const normalizedTitle = subsectionTitle.trim().toLowerCase();
  const sectionNumberMatch = normalizedTitle.match(/^(\d+(?:\.\d+)+)/);
  if (!sectionNumberMatch) return '';
  
  const sectionNumber = sectionNumberMatch[1];
  
  // Extract key words from the title (after the section number)
  const titleWords = normalizedTitle
    .replace(/^\d+(?:\.\d+)+\.?\s*/, '') // Remove section number
    .split(/\s+/)
    .filter(word => word.length > 2) // Filter out short words
    .slice(0, 5); // Take first 5 meaningful words
  
  // Try multiple patterns to find the subsection
  const patterns = [
    // Pattern 1: Match section number followed by content until next section number
    new RegExp(`(${sectionNumber.replace(/\./g, '\\.')}[^\\d]*?)(?=\\d+\\.[^\\d]|$)`, 'is'),
    // Pattern 2: Match section number in HTML headings
    new RegExp(`<h[1-6][^>]*>\\s*${sectionNumber.replace(/\./g, '\\.')}[^<]*</h[1-6]>([\\s\\S]*?)(?=<h[1-6]|\\d+\\.[^\\d]|$)`, 'is'),
    // Pattern 3: Match section number followed by title words
    new RegExp(`(${sectionNumber.replace(/\./g, '\\.')}[^\\d]*?(?:${titleWords.join('|')})[^\\d]*?)(?=\\d+\\.[^\\d]|$)`, 'is'),
    // Pattern 4: Match section number at start of line or paragraph
    new RegExp(`(?:^|\\n|\\r\\n|>)\\s*${sectionNumber.replace(/\./g, '\\.')}[^\\d]*?([\\s\\S]*?)(?=(?:^|\\n|\\r\\n|>)\\s*\\d+\\.[^\\d]|$)`, 'is'),
  ];
  
  for (const pattern of patterns) {
    const match = content.match(pattern);
    if (match) {
      const extracted = (match[1] || match[0]).trim();
      if (extracted && extracted.length > 20) { // Ensure we got meaningful content
        return extracted;
      }
    }
  }
  
  // Fallback: Try to find by section number in any format
  const fallbackPattern = new RegExp(`(${sectionNumber.replace(/\./g, '\\.')}[^\\d]*?)(?=\\d+\\.[^\\d]|$)`, 'is');
  const fallbackMatch = content.match(fallbackPattern);
  if (fallbackMatch && fallbackMatch[1]) {
    const extracted = fallbackMatch[1].trim();
    if (extracted && extracted.length > 20) {
      return extracted;
    }
  }
  
  return '';
};

/**
 * Gets the parent section title for a subsection
 * @param {string} subsectionTitle - The subsection title (e.g., "2.1 Purpose")
 * @returns {string} - Parent section title (e.g., "2. INTRODUCTION")
 */
const getParentSectionTitle = (subsectionTitle) => {
  const match = subsectionTitle.match(/^(\d+)\./);
  if (!match) return null;
  
  const mainSectionNumber = match[1];
  
  // Map of section numbers to main section titles
  const sectionMap = {
    '1': '1. PROTOCOL SUMMARY',
    '2': '2. INTRODUCTION',
    '3': '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
    '4': '4. TRIAL DESIGN',
    '5': '5. TRIAL POPULATION',
    '6': '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
    '7': '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL',
    '8': '8. TRIAL ASSESSMENTS AND PROCEDURES',
    '9': '9. ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS',
    '10': '10. STATISTICAL CONSIDERATIONS',
    '11': '11. TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS',
    '12': '12. APPENDIX: SUPPORTING DETAILS',
    '13': '13. APPENDIX: GLOSSARY OF TERMS AND ABBREVIATIONS',
    '14': '14. APPENDIX: REFERENCES',
  };
  
  return sectionMap[mainSectionNumber] || null;
};

/**
 * Fetches protocol sections dynamically based on ICF section mappings
 * This implements a RAG-based approach to retrieve relevant protocol context
 * Handles both main sections and subsections (extracts subsections from parent sections)
 * 
 * @param {string} projectId - The project ID
 * @param {Array} protocolSources - Array of protocol source mappings from ICF section data
 * @returns {Promise<string>} - Formatted context string containing relevant protocol sections
 */
export const fetchProtocolContextForICF = async (projectId, protocolSources) => {
  if (!projectId || !protocolSources || protocolSources.length === 0) {
    return '';
  }

  let contextParts = [];
  const fetchedSections = new Map(); // Cache to avoid duplicate fetches
  const parentSectionsCache = new Map(); // Cache parent sections for subsection extraction
  let allProtocolSections = null; // Lazy-loaded cache of all protocol sections for subsection matching

  try {
    // Iterate through each source mapping
    for (const source of protocolSources) {
      if (source.type === 'PROTOCOL' && source.titles && Array.isArray(source.titles)) {
        // Fetch all protocol sections for this mapping
        for (const title of source.titles) {
          // Check cache first
          const cacheKey = `${source.type}:${title}`;
          if (fetchedSections.has(cacheKey)) {
            const cached = fetchedSections.get(cacheKey);
            contextParts.push(`\n[${cached.title}]\n${cached.content}`);
            continue;
          }

          try {
            // Check if this is a subsection (has pattern like "2.1", "2.2", "11.3", etc.)
            const isSubsection = /^\d+\.\d+/.test(title);
            
            let section = null;
            let sectionContent = '';
            
            if (isSubsection) {
              // Lazily load all protocol sections once so we can search without hitting the API per subsection
              if (!allProtocolSections) {
                try {
                  allProtocolSections = await documentEditorService.getAllSections(projectId);
                } catch (err) {
                  const errorMessage = err?.message || err?.response?.data?.message || String(err);
                  console.warn(`Unable to load protocol sections for subsection matching:`, errorMessage);
                  allProtocolSections = [];
                }
              }

              if (allProtocolSections && allProtocolSections.length > 0) {
                const normalizedSearchTitle = title.trim().toLowerCase();
                const searchNumberMatch = normalizedSearchTitle.match(/^(\d+\.\d+)/);

                section = allProtocolSections.find(s => {
                  const sTitle = (s.title || '').trim().toLowerCase();
                  if (!sTitle) return false;

                  // Direct subsection number match (e.g., "5.2" matches "5.2 Inclusion Criteria")
                  if (searchNumberMatch && sTitle.startsWith(searchNumberMatch[1])) {
                    return true;
                  }

                  // Match all significant words after the section number
                  const searchWords = normalizedSearchTitle
                    .replace(/^(\d+(?:\.\d+)+\.?\s*)/, '')
                    .split(/\s+/)
                    .filter(w => w.length > 2);

                  if (searchWords.length === 0) {
                    return false;
                  }

                  return searchWords.every(word => sTitle.includes(word));
                });

                if (section && section.output) {
                  sectionContent = section.output;
                  console.log(`Found subsection "${section.title}" from cached section list for "${title}"`);
                }

                // If still not found, try to locate the subsection inside its parent section content
                if (!sectionContent && searchNumberMatch) {
                  const mainSectionNumber = searchNumberMatch[1].split('.')[0];
                  const parentCandidates = allProtocolSections.filter(s => {
                    const candidateNumber = extractSectionNumber(s.title || '');
                    return candidateNumber === mainSectionNumber;
                  });

                  for (const parentCandidate of parentCandidates) {
                    if (parentCandidate.output) {
                      const extractedContent = extractSubsection(parentCandidate.output, title);
                      if (extractedContent) {
                        section = { title, output: extractedContent };
                        sectionContent = extractedContent;
                        parentSectionsCache.set(parentCandidate.title, parentCandidate);
                        console.log(`Extracted subsection "${title}" from cached parent "${parentCandidate.title}"`);
                        break;
                      }
                    }
                  }
                }
              }

              // Absolute fallback: fetch parent section directly if not already retrieved
              if (!sectionContent) {
                const parentTitle = getParentSectionTitle(title);
                if (parentTitle) {
                  let parentSection = parentSectionsCache.get(parentTitle);

                  if (!parentSection) {
                    try {
                      parentSection = await documentEditorService.getSectionByTitle(parentTitle, projectId, 'PROTOCOL');
                      if (parentSection && parentSection.output) {
                        parentSectionsCache.set(parentTitle, parentSection);
                      }
                    } catch (error) {
                      const errorMessage = error?.message || error?.response?.data?.message || String(error);
                      console.warn(`Parent section "${parentTitle}" not found for subsection "${title}":`, errorMessage);
                      parentSection = null;
                    }
                  }

                  if (parentSection && parentSection.output) {
                    sectionContent = extractSubsection(parentSection.output, title);
                    if (sectionContent) {
                      section = { title, output: sectionContent };
                    } else {
                      console.warn(`Could not extract subsection "${title}" from parent section "${parentTitle}"`);
                    }
                  }
                }
              }
            } else {
              // For main sections, fetch normally
              section = await documentEditorService.getSectionByTitle(title, projectId, 'PROTOCOL');
              
              // If exact match fails, try variations
              if (!section) {
                const variations = [
                  title.replace(/^(\d+)\.\s*/, '$1. '),
                  title.replace(/^(\d+)\.\s*/, '$1. ').toUpperCase(),
                  title.replace(/\s+/g, ' ').trim(),
                  title.replace(/\./g, '. ').trim()
                ];
                
                for (const variation of variations) {
                  if (variation !== title) {
                    section = await documentEditorService.getSectionByTitle(variation, projectId, 'PROTOCOL');
                    if (section) break;
                  }
                }
              }
              
              // If still not found, try searching all sections (fallback)
              if (!section) {
                try {
                  const allSections = await documentEditorService.getAllSections(projectId);
                  if (allSections && allSections.length > 0) {
                    // Try multiple matching strategies
                    const normalizedSearchTitle = title.trim().toLowerCase();
                    
                    section = allSections.find(s => {
                      const sTitle = (s.title || '').trim();
                      const normalizedSTitle = sTitle.toLowerCase();
                      
                      // Exact match (case-insensitive)
                      if (normalizedSTitle === normalizedSearchTitle) return true;
                      
                      // Match by section number (e.g., "5.2" matches "5.2 Inclusion Criteria")
                      const searchNumberMatch = normalizedSearchTitle.match(/^(\d+\.\d+)/);
                      if (searchNumberMatch) {
                        const searchNumber = searchNumberMatch[1];
                        if (normalizedSTitle.startsWith(searchNumber)) return true;
                      }
                      
                      // Match if title contains all key words from search
                      const searchWords = normalizedSearchTitle.split(/\s+/).filter(w => w.length > 2);
                      if (searchWords.length > 0) {
                        const allWordsMatch = searchWords.every(word => normalizedSTitle.includes(word));
                        if (allWordsMatch && normalizedSTitle.includes(searchNumberMatch ? searchNumberMatch[1] : '')) {
                          return true;
                        }
                      }
                      
                      return false;
                    });
                    
                    if (section) {
                      console.log(`Found section "${section.title}" using fallback search for "${title}"`);
                    }
                  }
                } catch (err) {
                  const errorMessage = err?.message || err?.response?.data?.message || String(err);
                  console.warn(`Could not fetch all sections for fallback matching:`, errorMessage);
                }
              }
              
              if (section && section.output) {
                sectionContent = section.output;
                // Cache parent section for potential subsection extraction
                parentSectionsCache.set(title, section);
              }
            }

            if (section && sectionContent) {
              fetchedSections.set(cacheKey, { title: section.title || title, content: sectionContent });
              contextParts.push(`\n[${section.title || title}]\n${sectionContent}`);
            } else {
              // Only warn if it's not a subsection (subsections are expected to be extracted from parents)
              const isSubsection = /^\d+\.\d+/.test(title);
              if (!isSubsection) {
                console.warn(`Protocol section not found: "${title}" for project ${projectId}`);
              }
            }
          } catch (error) {
            // Handle different error types safely
            const errorMessage = error?.message || error?.response?.data?.message || String(error);
            const isSubsection = /^\d+\.\d+/.test(title);
            // Only log warnings for main sections, subsections are expected to fail and use fallback
            if (!isSubsection) {
              console.warn(`Error fetching protocol section "${title}":`, errorMessage);
            }
            // Continue with other sections even if one fails
          }
        }
      }
    }

    if (contextParts.length > 0) {
      return `\n\n=== RELEVANT PROTOCOL SECTIONS ===${contextParts.join('\n\n')}\n\n=== END PROTOCOL SECTIONS ===\n\n`;
    }

    return '';
  } catch (error) {
    console.error('Error fetching protocol context:', error);
    return '';
  }
};

/**
 * Helper function to extract section number from title for matching
 */
const extractSectionNumber = (title) => {
  const match = title.match(/^(\d+(?:\.\d+)*)/);
  return match ? match[1] : null;
};

